import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@monorepo/shared-auth/firebaseConfig';
import { useAuth } from '@monorepo/shared-auth';
import { addLeagueMember, getLeague } from '../../services/leagueService';
import { createAndSendInvitation } from '../../services/invitationService';
import { IconSearch, IconCircleX, IconPlus, IconMail, IconUserPlus } from '@tabler/icons-react';

const InviteMemberModal = ({ leagueId, league, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('quick'); // 'quick', 'search', or 'email'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Team-based league state
  const [inviteRole, setInviteRole] = useState('player'); // 'captain' or 'player'
  const [teamName, setTeamName] = useState('');

  // Email invitation state
  const [emailInvites, setEmailInvites] = useState([{ email: '', name: '' }]);
  const [sendingEmails, setSendingEmails] = useState(false);

  // Quick add state (for adding members without accounts/emails)
  const [quickAddMembers, setQuickAddMembers] = useState([{ firstName: '', lastName: '', email: '' }]);
  const [addingQuickMembers, setAddingQuickMembers] = useState(false);

  const isTeamLeague = league?.teamBased || false;

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setError('Please enter an email or name to search');
      return;
    }

    setSearching(true);
    setError('');
    setSearchResults([]);

    try {
      // Search by email (exact match)
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '==', searchTerm.toLowerCase().trim())
      );
      const emailSnapshot = await getDocs(emailQuery);

      const results = [];
      emailSnapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });

      // If no email match, try searching by name (partial match)
      if (results.length === 0) {
        const nameQuery = query(collection(db, 'users'));
        const nameSnapshot = await getDocs(nameQuery);

        nameSnapshot.forEach((doc) => {
          const data = doc.data();
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase();
          if (fullName.includes(searchTerm.toLowerCase().trim())) {
            results.push({ id: doc.id, ...data });
          }
        });
      }

      if (results.length === 0) {
        setError('No users found matching your search');
      } else {
        setSearchResults(results);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search for users. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.find(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleInvite = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user to invite');
      return;
    }

    // For team leagues inviting captains, require team name
    if (isTeamLeague && inviteRole === 'captain' && !teamName.trim()) {
      setError('Please enter a team name for the captain');
      return;
    }

    setInviting(true);
    setError('');

    try {
      const invitePromises = selectedUsers.map(user =>
        addLeagueMember(leagueId, user.id, {
          role: isTeamLeague ? inviteRole : 'player',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          teamName: isTeamLeague && inviteRole === 'captain' ? teamName.trim() : null,
          teamCaptainId: null,
          status: 'active'
        })
      );

      await Promise.all(invitePromises);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error inviting members:', err);
      setError('Failed to invite some members. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  // Email invitation handlers
  const addEmailInviteRow = () => {
    setEmailInvites([...emailInvites, { email: '', name: '', teamName: '' }]);
  };

  const removeEmailInviteRow = (index) => {
    if (emailInvites.length > 1) {
      setEmailInvites(emailInvites.filter((_, i) => i !== index));
    }
  };

  const updateEmailInvite = (index, field, value) => {
    const updated = [...emailInvites];
    updated[index][field] = value;
    setEmailInvites(updated);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Quick Add handlers
  const addQuickMemberRow = () => {
    setQuickAddMembers([...quickAddMembers, { firstName: '', lastName: '', email: '' }]);
  };

  const removeQuickMemberRow = (index) => {
    if (quickAddMembers.length > 1) {
      setQuickAddMembers(quickAddMembers.filter((_, i) => i !== index));
    }
  };

  const updateQuickMember = (index, field, value) => {
    const updated = [...quickAddMembers];
    updated[index][field] = value;
    setQuickAddMembers(updated);
  };

  const generateGuestId = () => {
    return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleQuickAdd = async () => {
    setError('');
    setSuccessMessage('');

    // Filter out empty entries (must have at least first name)
    const validMembers = quickAddMembers.filter(m => m.firstName.trim());

    if (validMembers.length === 0) {
      setError('Please enter at least one member name');
      return;
    }

    // For team leagues inviting captains, require team name
    if (isTeamLeague && inviteRole === 'captain' && !teamName.trim()) {
      setError('Please enter a team name for the captain');
      return;
    }

    setAddingQuickMembers(true);

    try {
      const addPromises = validMembers.map(member => {
        const guestId = generateGuestId();
        return addLeagueMember(leagueId, guestId, {
          role: isTeamLeague ? inviteRole : 'player',
          firstName: member.firstName.trim(),
          lastName: member.lastName.trim(),
          email: member.email.trim() || null,
          teamName: isTeamLeague && inviteRole === 'captain' ? teamName.trim() : null,
          teamCaptainId: null,
          status: 'active',
          isGuest: true // Flag to indicate this is a guest/offline member
        });
      });

      await Promise.all(addPromises);

      setSuccessMessage(`Successfully added ${validMembers.length} member${validMembers.length > 1 ? 's' : ''}!`);

      // Reset and close after brief delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding members:', err);
      setError('Failed to add some members. Please try again.');
    } finally {
      setAddingQuickMembers(false);
    }
  };

  const handleSendEmailInvitations = async () => {
    setError('');
    setSuccessMessage('');

    // Validate emails
    const validInvites = emailInvites.filter(inv => validateEmail(inv.email));

    if (validInvites.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    // For team leagues inviting captains, require team name
    if (isTeamLeague && inviteRole === 'captain' && !teamName.trim()) {
      setError('Please enter a team name for the captain');
      return;
    }

    setSendingEmails(true);

    try {
      // Get league details
      const league = await getLeague(leagueId);
      if (!league) {
        throw new Error('League not found');
      }

      // Get current user's name
      const inviterName = currentUser?.displayName ||
                         `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
                         'A UBsports member';

      // Send invitations
      const invitationPromises = validInvites.map(invite =>
        createAndSendInvitation({
          leagueId,
          leagueName: league.name,
          sport: league.sport,
          inviterName,
          inviterId: currentUser.uid,
          inviteeEmail: invite.email,
          inviteeName: invite.name,
          role: isTeamLeague ? inviteRole : 'player',
          teamName: isTeamLeague && inviteRole === 'captain' ? teamName.trim() : null
        })
      );

      await Promise.all(invitationPromises);

      setSuccessMessage(`Successfully sent ${validInvites.length} invitation${validInvites.length > 1 ? 's' : ''}!`);

      // Reset form after 2 seconds
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error sending email invitations:', err);
      setError('Failed to send some invitations. Please try again.');
    } finally {
      setSendingEmails(false);
    }
  };

  return (
    <section className="ls-invite-member-modal">
      <section className="ls-modal-content">
        <header className="ls-modal-header">
          <h2>Invite Members</h2>
          <button onClick={onClose} className="ls-close-btn" aria-label="Close modal">
            <IconCircleX size={24} />
          </button>
        </header>

        {/* Tab Navigation */}
        <div className="ls-modal-tabs">
          <button
            className={`ls-tab-btn ${activeTab === 'quick' ? 'ls-active' : ''}`}
            onClick={() => {
              setActiveTab('quick');
              setError('');
              setSuccessMessage('');
            }}
          >
            <IconUserPlus size={18} />
            Quick Add
          </button>
          <button
            className={`ls-tab-btn ${activeTab === 'search' ? 'ls-active' : ''}`}
            onClick={() => {
              setActiveTab('search');
              setError('');
              setSuccessMessage('');
            }}
          >
            <IconSearch size={18} />
            Search Users
          </button>
          <button
            className={`ls-tab-btn ${activeTab === 'email' ? 'ls-active' : ''}`}
            onClick={() => {
              setActiveTab('email');
              setError('');
              setSuccessMessage('');
            }}
          >
            <IconMail size={18} />
            Invite by Email
          </button>
        </div>

        {error && (
          <section className="ls-error-message" role="alert">{error}</section>
        )}

        {successMessage && (
          <section className="ls-success-message" role="alert">{successMessage}</section>
        )}

        {/* Quick Add Tab */}
        {activeTab === 'quick' && (
          <>
            <div className="ls-quick-add-form">
              <p className="ls-form-description">
                Quickly add members by name. Email is optional — perfect for family tournaments
                or casual leagues where you just need to track participants.
              </p>

              {isTeamLeague && (
                <section className="ls-team-invite-options">
                  <div className="ls-role-selection">
                    <label>Add as:</label>
                    <div className="ls-role-buttons">
                      <button
                        type="button"
                        className={`ls-role-btn ${inviteRole === 'player' ? 'ls-active' : ''}`}
                        onClick={() => setInviteRole('player')}
                      >
                        Player
                      </button>
                      <button
                        type="button"
                        className={`ls-role-btn ${inviteRole === 'captain' ? 'ls-active' : ''}`}
                        onClick={() => setInviteRole('captain')}
                      >
                        Team Captain
                      </button>
                    </div>
                  </div>

                  {inviteRole === 'captain' && (
                    <div className="ls-team-name-input-wrapper">
                      <label htmlFor="teamNameQuick">Team Name *</label>
                      <input
                        type="text"
                        id="teamNameQuick"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g., Thunder Strikers"
                        className="ls-team-name-input"
                      />
                    </div>
                  )}
                </section>
              )}

              {quickAddMembers.map((member, index) => (
                <div key={index} className="ls-quick-add-row">
                  <div className="ls-quick-add-inputs">
                    <input
                      type="text"
                      placeholder="First name *"
                      value={member.firstName}
                      onChange={(e) => updateQuickMember(index, 'firstName', e.target.value)}
                      className="ls-input-first-name"
                    />
                    <input
                      type="text"
                      placeholder="Last name"
                      value={member.lastName}
                      onChange={(e) => updateQuickMember(index, 'lastName', e.target.value)}
                      className="ls-input-last-name"
                    />
                    <input
                      type="email"
                      placeholder="Email (optional)"
                      value={member.email}
                      onChange={(e) => updateQuickMember(index, 'email', e.target.value)}
                      className="ls-input-email-optional"
                    />
                  </div>
                  {quickAddMembers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuickMemberRow(index)}
                      className="ls-remove-row-btn"
                      aria-label="Remove member"
                    >
                      <IconCircleX size={20} />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addQuickMemberRow}
                className="ls-add-member-btn"
              >
                <IconPlus size={18} />
                Add Another Member
              </button>
            </div>

            <footer className="ls-modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="ls-btn-secondary"
                disabled={addingQuickMembers}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleQuickAdd}
                className="ls-btn-primary"
                disabled={addingQuickMembers}
              >
                {addingQuickMembers ? 'Adding...' : `Add ${quickAddMembers.filter(m => m.firstName.trim()).length || ''} Member${quickAddMembers.filter(m => m.firstName.trim()).length !== 1 ? 's' : ''}`}
              </button>
            </footer>
          </>
        )}

        {/* Search Users Tab */}
        {activeTab === 'search' && (
          <>
            <form onSubmit={handleSearch} className="ls-search-form">
          <div className="ls-search-input-wrapper">
            <IconSearch size={20} className="ls-search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by email or name..."
              className="ls-search-input"
            />
          </div>
          <button
            type="submit"
            className="ls-btn-search"
            disabled={searching || !searchTerm.trim()}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </form>

            {isTeamLeague && (
              <section className="ls-team-invite-options">
                <div className="ls-role-selection">
                  <label>Invite as:</label>
                  <div className="ls-role-buttons">
                    <button
                      type="button"
                      className={`ls-role-btn ${inviteRole === 'player' ? 'ls-active' : ''}`}
                      onClick={() => setInviteRole('player')}
                    >
                      Player
                    </button>
                    <button
                      type="button"
                      className={`ls-role-btn ${inviteRole === 'captain' ? 'ls-active' : ''}`}
                      onClick={() => setInviteRole('captain')}
                    >
                      Team Captain
                    </button>
                  </div>
                </div>

                {inviteRole === 'captain' && (
                  <div className="ls-team-name-input-wrapper">
                    <label htmlFor="teamName">Team Name *</label>
                    <input
                      type="text"
                      id="teamName"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="e.g., Thunder Strikers"
                      className="ls-team-name-input"
                    />
                  </div>
                )}
              </section>
            )}

            {selectedUsers.length > 0 && (
              <section className="ls-selected-users">
                <h3>Selected ({selectedUsers.length})</h3>
                <div className="ls-selected-users-list">
                  {selectedUsers.map(user => (
                    <div key={user.id} className="ls-selected-user-chip">
                      <span>{user.firstName} {user.lastName}</span>
                      <button
                        type="button"
                        onClick={() => toggleUserSelection(user)}
                        className="ls-remove-chip-btn"
                        aria-label={`Remove ${user.firstName} ${user.lastName}`}
                      >
                        <IconCircleX size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {searchResults.length > 0 && (
              <section className="ls-search-results">
                <h3>Search Results</h3>
                <div className="ls-results-list">
                  {searchResults.map(user => {
                    const isSelected = selectedUsers.find(u => u.id === user.id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        className={`ls-user-result-card ${isSelected ? 'ls-selected' : ''}`}
                        onClick={() => toggleUserSelection(user)}
                      >
                        <div className="ls-user-info">
                          <div className="ls-user-avatar">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                          <div className="ls-user-details">
                            <h4>{user.firstName} {user.lastName}</h4>
                            <p>{user.email}</p>
                          </div>
                        </div>
                        <div className="ls-user-action">
                          {isSelected ? (
                            <span className="ls-selected-badge">Selected</span>
                          ) : (
                            <IconPlus size={20} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {selectedUsers.length > 0 && (
              <footer className="ls-modal-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="ls-btn-secondary"
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleInvite}
                  className="ls-btn-primary"
                  disabled={inviting}
                >
                  {inviting ? 'Inviting...' : `Invite ${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}`}
                </button>
              </footer>
            )}
          </>
        )}

        {/* Email Invitation Tab */}
        {activeTab === 'email' && (
          <>
            {isTeamLeague && (
              <section className="ls-team-invite-options">
                <div className="ls-role-selection">
                  <label>Invite as:</label>
                  <div className="ls-role-buttons">
                    <button
                      type="button"
                      className={`ls-role-btn ${inviteRole === 'player' ? 'ls-active' : ''}`}
                      onClick={() => setInviteRole('player')}
                    >
                      Player
                    </button>
                    <button
                      type="button"
                      className={`ls-role-btn ${inviteRole === 'captain' ? 'ls-active' : ''}`}
                      onClick={() => setInviteRole('captain')}
                    >
                      Team Captain
                    </button>
                  </div>
                </div>

                {inviteRole === 'captain' && (
                  <div className="ls-team-name-input-wrapper">
                    <label htmlFor="teamNameEmail">Team Name *</label>
                    <input
                      type="text"
                      id="teamNameEmail"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="e.g., Thunder Strikers"
                      className="ls-team-name-input"
                    />
                  </div>
                )}
              </section>
            )}

            <div className="ls-email-invite-form">
              <p className="ls-form-description">
                Send email invitations to people who don't have a UBsports account yet.
                They'll receive an email with a link to join your league.
              </p>

              {emailInvites.map((invite, index) => (
                <div key={index} className="ls-email-invite-row">
                  <div className="ls-email-input-group">
                    <input
                      type="email"
                      placeholder="Email address *"
                      value={invite.email}
                      onChange={(e) => updateEmailInvite(index, 'email', e.target.value)}
                      className="ls-email-input"
                    />
                    <input
                      type="text"
                      placeholder="Name (optional)"
                      value={invite.name}
                      onChange={(e) => updateEmailInvite(index, 'name', e.target.value)}
                      className="ls-name-input"
                    />
                  </div>
                  {emailInvites.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEmailInviteRow(index)}
                      className="ls-remove-row-btn"
                      aria-label="Remove email invite"
                    >
                      <IconCircleX size={20} />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addEmailInviteRow}
                className="ls-add-email-btn"
              >
                <IconPlus size={18} />
                Add Another Email
              </button>
            </div>

            <footer className="ls-modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="ls-btn-secondary"
                disabled={sendingEmails}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendEmailInvitations}
                className="ls-btn-primary"
                disabled={sendingEmails}
              >
                {sendingEmails ? 'Sending...' : 'Send Invitations'}
              </button>
            </footer>
          </>
        )}
      </section>
    </section>
  );
};

export default InviteMemberModal;
